import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function About() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [hash]);

  return (
    <Layout transparentHeader>
      {/* Wide hero */}
      <section className="relative w-full aspect-[16/9] min-h-[520px] bg-stone">
        <img
          src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20photograph%20of%20a%20quiet%20jewelry%20atelier%20interior%20in%20Antwerp%20with%20warm%20natural%20light%2C%20wooden%20workbench%2C%20small%20brass%20tools%2C%20muted%20neutral%20tones%2C%20refined%20contemporary%20jewelry%20house%20mood%2C%20editorial%20photography&width=900&height=506&seq=aurelin-about-hero&orientation=landscape"
          alt="Aurelin atelier, Antwerp"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 shell h-full flex flex-col justify-end pb-24 text-canvas">
          <p className="eyebrow text-canvas/80 mb-4">Our House</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.05] max-w-3xl">A small house, quietly built.</h1>
        </div>
      </section>

      {/* Breadcrumb strip */}
      <div className="shell pt-10">
        <Breadcrumb items={[{ label: "About" }]} />
      </div>

      {/* 50/50 — Our Story */}
      <section id="story">
        <div className="shell py-32 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="eyebrow mb-4">Origin</p>
            <h2 className="font-display text-4xl leading-tight text-black">Founded in Antwerp, 2019.</h2>
            <p className="mt-6 text-graphite leading-[1.9] text-[15px] max-w-lg">
              Aurelin began as a single workbench and a small collection of fifteen sketches. Today, we
              remain a five-person atelier — designing, casting and finishing each piece under one roof.
            </p>
          </div>
          <div className="w-full aspect-[4/5] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Portrait%20of%20a%20jewelry%20designer%20in%20a%20minimalist%20Antwerp%20atelier%2C%20soft%20natural%20window%20light%2C%20warm%20neutral%20interior%2C%20muted%20palette%2C%20refined%20contemporary%20jewelry%20house%2C%20editorial%20photography&width=500&height=625&seq=aurelin-about-01&orientation=portrait"
              alt="Founder in the Aurelin atelier"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Wide campaign */}
      <section className="bg-stone">
        <div className="w-full aspect-[16/7] min-h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20detail%20photograph%20of%20hand%20polishing%20a%20small%20gold%20ring%20on%20a%20wooden%20workbench%2C%20warm%20natural%20light%2C%20muted%20neutral%20tones%2C%20refined%20contemporary%20jewelry%20atelier%2C%20editorial%20quiet%20luxury%20photography&width=900&height=395&seq=aurelin-about-campaign&orientation=landscape"
            alt="Hand-finishing at the Aurelin atelier"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </section>

      {/* Materials — LEFT BLEED */}
      <section id="materials" className="py-28">
        <div
          className="w-full grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center px-5 md:pl-0"
          style={{ paddingRight: "max(20px, calc((100vw - 1320px) / 2 + 48px))" }}
        >
          <div className="md:col-span-7 relative">
            <div className="relative w-full aspect-[4/5] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20woman%20wearing%20delicate%20gold%20jewelry%20standing%20by%20a%20soft%20window%20in%20a%20quiet%20interior%2C%20warm%20natural%20light%2C%20muted%20neutral%20palette%2C%20contemporary%20jewelry%20campaign%20photography&width=560&height=700&seq=aurelin-about-02&orientation=portrait"
                alt="Aurelin campaign portrait"
                className="w-full h-full object-cover object-top transition-transform duration-500 ease-out"
              />
              <div
                aria-hidden
                className="hidden md:block absolute inset-y-0 right-0 w-2/5 pointer-events-none"
                style={{ background: "linear-gradient(to left, #FDFDFD 0%, rgba(253,253,253,0.6) 45%, rgba(253,253,253,0) 100%)" }}
              />
            </div>
          </div>
          <div className="md:col-span-5 md:pl-4">
            <p className="eyebrow mb-4">Material</p>
            <h2 className="font-display text-4xl leading-tight text-black">Recycled precious metals, always.</h2>
            <p className="mt-6 text-graphite leading-[1.9] text-[15px] max-w-[46ch]">
              We work exclusively with certified recycled 18k gold, platinum and sterling silver. Stones are
              lab-grown or ethically sourced with full traceability documentation.
            </p>
          </div>
        </div>
      </section>

      {/* Small Runs — RIGHT BLEED */}
      <section id="practice" className="py-28">
        <div
          className="w-full grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center px-5 md:pr-0"
          style={{ paddingLeft: "max(20px, calc((100vw - 1320px) / 2 + 48px))" }}
        >
          <div className="md:col-span-5 md:pr-4 order-2 md:order-1">
            <p className="eyebrow mb-4">Practice</p>
            <h2 className="font-display text-4xl leading-tight text-black">Small runs, hand finished.</h2>
            <p className="mt-6 text-graphite leading-[1.9] text-[15px] max-w-[46ch]">
              Nothing is mass-produced. Each collection releases in editions of thirty to a hundred pieces,
              made-to-order and delivered within seven days from our Antwerp studio.
            </p>
          </div>
          <div className="md:col-span-7 relative order-1 md:order-2">
            <div className="relative w-full aspect-[4/5] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20detail%20photograph%20of%20jeweler%20hands%20setting%20a%20small%20stone%20into%20a%20thin%20gold%20ring%2C%20warm%20natural%20light%2C%20muted%20neutral%20tones%2C%20refined%20contemporary%20jewelry%20atelier%2C%20editorial%20photography&width=560&height=700&seq=aurelin-about-03&orientation=portrait"
                alt="Stone setting in the atelier"
                className="w-full h-full object-cover object-top transition-transform duration-500 ease-out"
              />
              <div
                aria-hidden
                className="hidden md:block absolute inset-y-0 left-0 w-2/5 pointer-events-none"
                style={{ background: "linear-gradient(to right, #FDFDFD 0%, rgba(253,253,253,0.6) 45%, rgba(253,253,253,0) 100%)" }}
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}